import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";

const MotionCard = motion(Card);

/* shared glass mixin */
const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const CARD_CONFIG = [
  {
    key: "average_score",
    label: "Average Score",
    icon: <TrendingUpRoundedIcon />,
    gradient: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    color: "#6366f1",
    lightColor: "rgba(99,102,241,0.1)",
    glowColor: "rgba(99,102,241,0.25)",
    suffix: "%",
    max: 100,
  },
  {
    key: "pass_rate",
    label: "Pass Rate",
    icon: <CheckCircleRoundedIcon />,
    gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    color: "#10b981",
    lightColor: "rgba(16,185,129,0.1)",
    glowColor: "rgba(16,185,129,0.25)",
    suffix: "%",
    max: 100,
  },
  {
    key: "total_records",
    label: "Total Records",
    icon: <BarChartRoundedIcon />,
    gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    color: "#f59e0b",
    lightColor: "rgba(245,158,11,0.1)",
    glowColor: "rgba(245,158,11,0.25)",
    suffix: "",
    max: null,
  },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <Box
        sx={{
          background: "#1e1b4b",
          color: "#fff",
          px: 1.5,
          py: 1,
          borderRadius: "10px",
          fontSize: "0.78rem",
          fontWeight: 600,
        }}
      >
        {payload[0].value}
      </Box>
    );
  }
  return null;
};

const StatCard = ({ config, value, index }) => {
  const percentage = config.max
    ? Math.min((value / config.max) * 100, 100)
    : null;
  const chartData = config.max
    ? [
        { name: config.label, value: percentage, fill: config.color },
        { name: "remaining", value: 100 - percentage, fill: "transparent" },
      ]
    : null;

  return (
    <MotionCard
      initial={{ opacity: 0, y: 32, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: index * 0.12,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      sx={{
        flex: "1 1 210px",
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
        cursor: "default",
        borderRadius: "24px",
        ...glassMixin,
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: `0 20px 48px ${config.glowColor}, 0 1px 0 rgba(255,255,255,0.9) inset`,
        },
      }}
    >
      {/* top glow bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: config.gradient,
          opacity: 0.9,
        }}
      />

      {/* floating orb */}
      <Box
        sx={{
          position: "absolute",
          top: -30,
          right: -30,
          width: 110,
          height: 110,
          borderRadius: "50%",
          background: config.lightColor,
          filter: "blur(20px)",
        }}
      />

      <CardContent sx={{ p: "22px !important", position: "relative" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            {/* icon */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                background: config.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                mb: 2.5,
                boxShadow: `0 6px 16px ${config.glowColor}`,
                "& svg": { fontSize: 22 },
              }}
            >
              {config.icon}
            </Box>

            <Typography
              sx={{
                color: "#9ca3af",
                letterSpacing: "0.08em",
                fontSize: "0.68rem",
                fontWeight: 700,
                textTransform: "uppercase",
                mb: 0.5,
                fontFamily: "'Syne', sans-serif",
              }}
            >
              {config.label}
            </Typography>

            <Typography
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 800,
                color: "#111827",
                mt: 0.5,
                lineHeight: 1,
                fontSize: { xs: "2rem", sm: "2.4rem" },
                letterSpacing: "-0.04em",
              }}
            >
              {value != null ? (
                <>
                  {typeof value === "number"
                    ? value.toFixed(config.max ? 1 : 0)
                    : value}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: config.color,
                      ml: 0.5,
                    }}
                  >
                    {config.suffix}
                  </Typography>
                </>
              ) : (
                <Skeleton width={80} height={48} />
              )}
            </Typography>
          </Box>

          {/* radial mini chart */}
          {chartData && value != null && (
            <Box sx={{ width: 62, height: 62, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="100%"
                  data={chartData}
                  startAngle={90}
                  endAngle={-270}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={4}
                    background={{ fill: config.lightColor }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadialBarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Stack>

        {/* progress bar */}
        {config.max && value != null && (
          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                height: 4,
                background: "rgba(0,0,0,0.06)",
                borderRadius: "100px",
                overflow: "hidden",
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{
                  duration: 1.4,
                  delay: index * 0.12 + 0.4,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  height: "100%",
                  background: config.gradient,
                  borderRadius: "100px",
                }}
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </MotionCard>
  );
};

const OverviewCardsSkeleton = () => (
  <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
    {[...Array(3)].map((_, i) => (
      <Card
        key={i}
        sx={{ flex: "1 1 210px", borderRadius: "24px", ...glassMixin }}
      >
        <CardContent sx={{ p: "22px !important" }}>
          <Skeleton
            variant="rounded"
            width={44}
            height={44}
            sx={{ borderRadius: "14px", mb: 2.5 }}
          />
          <Skeleton width="55%" height={12} sx={{ mb: 1 }} />
          <Skeleton width="75%" height={44} />
          <Skeleton
            width="100%"
            height={4}
            sx={{ mt: 2.5, borderRadius: "100px" }}
          />
        </CardContent>
      </Card>
    ))}
  </Box>
);

const OverviewCards = ({ overview }) => {
  if (!overview) return <OverviewCardsSkeleton />;
  return (
    <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
      {CARD_CONFIG.map((config, index) => (
        <StatCard
          key={config.key}
          config={config}
          value={overview[config.key] ?? null}
          index={index}
        />
      ))}
    </Box>
  );
};

export default OverviewCards;
